import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ThumbsUp, 
  Users, 
  MessageSquare, 
  Plus, 
  Loader2,
  Cat,
  Bird,
  Fish,
  Dog,
  Heart,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pet type icons
const PetIcons = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  fish: Fish,
  other: Heart
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'under_review': return 'bg-blue-100 text-blue-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'launched': return 'bg-purple-100 text-purple-800';
    case 'declined': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const ClubRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [votingId, setVotingId] = useState(null);
  
  // New request form
  const [newRequest, setNewRequest] = useState({
    pet_type: '',
    club_name: '',
    description: '',
    volunteer_to_help: false,
    volunteer_skills: ''
  });

  // Vote form
  const [voteVolunteer, setVoteVolunteer] = useState(false);
  const [voteSkills, setVoteSkills] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/club-requests?sort_by=votes`);
      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      }
    } catch (err) {
      console.error('Failed to fetch club requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to submit a request');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/club-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newRequest)
      });

      if (res.ok) {
        toast.success('Club request submitted! Others can now vote for it.');
        setShowNewRequest(false);
        setNewRequest({
          pet_type: '',
          club_name: '',
          description: '',
          volunteer_to_help: false,
          volunteer_skills: ''
        });
        fetchRequests();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit request');
      }
    } catch (err) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (requestId) => {
    if (!user) {
      toast.error('Please sign in to vote');
      return;
    }

    setVotingId(requestId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/club-requests/${requestId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          volunteer_to_help: voteVolunteer,
          volunteer_skills: voteSkills
        })
      });

      if (res.ok) {
        toast.success(voteVolunteer ? 'Vote cast and volunteered!' : 'Vote cast!');
        setVoteVolunteer(false);
        setVoteSkills('');
        fetchRequests();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to vote');
      }
    } catch (err) {
      toast.error('Failed to vote');
    } finally {
      setVotingId(null);
    }
  };

  const hasVoted = (request) => {
    return user && request.voters?.includes(user.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm mb-4">
          <Sparkles className="w-4 h-4" />
          Help Shape Our Community
        </div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
          Request a New Club
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Have other pets besides dogs? Vote for new clubs or request one for your pet type!
          We're building toward <strong>allamerican.pet</strong> - a community for all pet lovers.
        </p>
      </div>

      {/* New Request Button */}
      <div className="flex justify-center mb-8">
        <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="new-club-request-btn">
              <Plus className="w-4 h-4" />
              Request a New Club
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Request a New Pet Club</DialogTitle>
              <DialogDescription>
                Tell us about the club you'd like to see! If enough members vote, we'll build it.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitRequest} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="pet_type">Pet Type</Label>
                <Input
                  id="pet_type"
                  placeholder="e.g., rabbit, horse, reptile, hamster..."
                  value={newRequest.pet_type}
                  onChange={(e) => setNewRequest({ ...newRequest, pet_type: e.target.value })}
                  required
                  data-testid="club-pet-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="club_name">Suggested Club Name</Label>
                <Input
                  id="club_name"
                  placeholder="e.g., All American Rabbit Club"
                  value={newRequest.club_name}
                  onChange={(e) => setNewRequest({ ...newRequest, club_name: e.target.value })}
                  required
                  data-testid="club-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Why should this club exist?</Label>
                <Textarea
                  id="description"
                  placeholder="Tell us about the community need, potential features, etc."
                  value={newRequest.description}
                  onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                  rows={3}
                  required
                  data-testid="club-description-input"
                />
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="volunteer"
                    checked={newRequest.volunteer_to_help}
                    onCheckedChange={(checked) => setNewRequest({ ...newRequest, volunteer_to_help: checked })}
                    data-testid="volunteer-checkbox"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="volunteer" className="font-medium cursor-pointer">
                      I want to help build this club!
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Volunteers may help with content, moderation, or testing.
                    </p>
                  </div>
                </div>
                
                {newRequest.volunteer_to_help && (
                  <div className="mt-3 ml-6">
                    <Label htmlFor="skills">What skills can you contribute?</Label>
                    <Input
                      id="skills"
                      placeholder="e.g., rabbit breeder, content writing, photography..."
                      value={newRequest.volunteer_skills}
                      onChange={(e) => setNewRequest({ ...newRequest, volunteer_skills: e.target.value })}
                      className="mt-1"
                      data-testid="volunteer-skills-input"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNewRequest(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-club-request-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Existing Requests */}
      {requests.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Heart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No club requests yet</h3>
            <p className="text-muted-foreground mb-4">
              Be the first to request a new pet club!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-primary" />
            Active Requests ({requests.length})
          </h2>
          
          {requests.map((request) => {
            const Icon = PetIcons[request.pet_type] || PetIcons.other;
            const voted = hasVoted(request);
            
            return (
              <Card key={request.id} className="overflow-hidden" data-testid={`club-request-${request.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-lg">{request.club_name}</h3>
                        <Badge className={getStatusColor(request.status)}>
                          {request.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Pet type: <span className="font-medium">{request.pet_type}</span>
                      </p>
                      <p className="text-sm text-foreground mb-4">{request.description}</p>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4" />
                          {request.vote_count} votes
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {request.volunteers?.length || 0} volunteers
                        </span>
                        {request.forum_topic_id && (
                          <a href={`/forums/topic/${request.forum_topic_id}`} className="flex items-center gap-1 text-primary hover:underline">
                            <MessageSquare className="w-4 h-4" />
                            Forum discussion
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {/* Vote Button */}
                    <div className="flex-shrink-0">
                      {voted ? (
                        <Button variant="outline" disabled className="gap-2">
                          <ThumbsUp className="w-4 h-4 fill-current" />
                          Voted
                        </Button>
                      ) : (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="gap-2" data-testid={`vote-btn-${request.id}`}>
                              <ThumbsUp className="w-4 h-4" />
                              Vote
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>Vote for {request.club_name}</DialogTitle>
                              <DialogDescription>
                                Your vote helps prioritize which clubs we build next!
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="flex items-start space-x-3">
                                <Checkbox
                                  id={`volunteer-${request.id}`}
                                  checked={voteVolunteer}
                                  onCheckedChange={setVoteVolunteer}
                                />
                                <div className="space-y-1">
                                  <Label htmlFor={`volunteer-${request.id}`} className="font-medium cursor-pointer">
                                    I want to help build this club!
                                  </Label>
                                  <p className="text-sm text-muted-foreground">
                                    Optional - volunteers may help with content or testing.
                                  </p>
                                </div>
                              </div>
                              
                              {voteVolunteer && (
                                <div className="ml-6">
                                  <Label htmlFor={`skills-${request.id}`}>Your skills</Label>
                                  <Input
                                    id={`skills-${request.id}`}
                                    placeholder="How can you help?"
                                    value={voteSkills}
                                    onChange={(e) => setVoteSkills(e.target.value)}
                                    className="mt-1"
                                  />
                                </div>
                              )}
                              
                              <div className="flex justify-end gap-2">
                                <Button
                                  onClick={() => handleVote(request.id)}
                                  disabled={votingId === request.id}
                                >
                                  {votingId === request.id ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Voting...
                                    </>
                                  ) : (
                                    'Cast Vote'
                                  )}
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                  
                  {/* Volunteers list */}
                  {request.volunteers?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium mb-2">Volunteers:</p>
                      <div className="flex flex-wrap gap-2">
                        {request.volunteers.map((v, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {v.username}
                            {v.skills && ` - ${v.skills}`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info Section */}
      <Card className="mt-8 bg-muted/50">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-2">How does this work?</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>1. Request a new club for your pet type or vote for existing requests</li>
            <li>2. When a request gets enough votes and volunteers, we'll start building!</li>
            <li>3. Volunteers help shape the club's features and content</li>
            <li>4. Launched clubs get their own community under <strong>allamerican.pet</strong></li>
          </ul>
          <p className="text-sm text-muted-foreground mt-4">
            <strong>Note:</strong> Even while we build new clubs, you can already register all your pets 
            (including cats, birds, fish, and others) in the Dog Club and use all the tools!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClubRequests;
